"use client";

import React, { useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ResponsiveSelect } from "@/components/ui/responsive-select";
import { X, Users2 } from "lucide-react";

interface DualGroupingSelectorProps {
  primaryField: string | null;
  secondaryField: string | null;
  groupableFields: Array<{
    fieldId: string;
    label: string;
    type: string;
    groupingOptions?: any;
  }>;
  onPrimaryChange: (field: string | null) => void;
  onSecondaryChange: (field: string | null) => void;
  onClear?: () => void;
  className?: string;
}

/**
 * Component for selecting dual grouping fields (primary and secondary)
 * Allows users to group data hierarchically by two fields
 */
export const DualGroupingSelector = React.memo(function DualGroupingSelector({
  primaryField,
  secondaryField,
  groupableFields,
  onPrimaryChange,
  onSecondaryChange,
  onClear,
  className,
}: DualGroupingSelectorProps) {
  // Available secondary fields (exclude primary field)
  const availableSecondaryFields = useMemo(() => {
    return groupableFields.filter((field) => field.fieldId !== primaryField);
  }, [groupableFields, primaryField]);

  // Handlers with useCallback for performance
  const handlePrimaryChange = useCallback(
    (value: string) => {
      const newPrimary = value === "none" ? null : value;
      onPrimaryChange(newPrimary);

      // Clear secondary if it's the same as new primary
      if (secondaryField === newPrimary) {
        onSecondaryChange(null);
      }
    },
    [onPrimaryChange, onSecondaryChange, secondaryField]
  );

  const handleSecondaryChange = useCallback(
    (value: string) => {
      const newSecondary = value === "none" ? null : value;
      onSecondaryChange(newSecondary);
    },
    [onSecondaryChange]
  );

  const handleClearAll = useCallback(() => {
    onPrimaryChange(null);
    onSecondaryChange(null);
    onClear?.();
  }, [onPrimaryChange, onSecondaryChange, onClear]);

  const hasAnyGrouping = primaryField || secondaryField;

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 dark:from-blue-950/40 dark:to-purple-950/40 dark:border-blue-800 rounded-lg",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
        <Users2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <span>Group by:</span>
      </div>

      {/* Primary Field Selector */}
      <div className="flex items-center gap-2">
        <ResponsiveSelect
          value={primaryField || "none"}
          onValueChange={handlePrimaryChange}
          className="w-40"
          options={[
            { value: "none", label: "Select primary..." },
            ...groupableFields.map((field) => ({ value: field.fieldId, label: field.label })),
          ]}
        />
      </div>

      {/* Then By Connector */}
      {primaryField && (
        <>
          <span className="text-sm text-muted-foreground">then by</span>

          {/* Secondary Field Selector */}
          <div className="flex items-center gap-2">
            <ResponsiveSelect
              value={secondaryField || "none"}
              onValueChange={handleSecondaryChange}
              className="w-40"
              disabled={!primaryField}
              options={[
                { value: "none", label: "Select secondary..." },
                ...availableSecondaryFields.map((field) => ({
                  value: field.fieldId,
                  label: field.label,
                })),
              ]}
            />
          </div>
        </>
      )}

      {/* Clear Button */}
      {hasAnyGrouping && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/40 dark:hover:text-red-300"
          title="Clear all grouping"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      {/* Grouping Status Indicator */}
      {primaryField && secondaryField && (
        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-xs font-medium rounded">
          Dual Grouping Active
        </div>
      )}
      {primaryField && !secondaryField && (
        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 text-xs font-medium rounded">
          Single Grouping Active
        </div>
      )}
    </div>
  );
});

DualGroupingSelector.displayName = "DualGroupingSelector";

export default DualGroupingSelector;
